<template>
    <div class="rtb-schedule-grid">
        <div class="schedule-header">
            <span class="schedule-title">Расписание показов</span>
            <span class="schedule-hint">{{ activeHoursTotal }}ч из {{ totalAvailableHours }}ч активно</span>
        </div>

        <div v-if="Object.keys(visibleDays).length === 0" class="schedule-empty">
            Укажите даты начала и окончания кампании, чтобы настроить расписание.
        </div>

        <div v-else class="schedule-table">
            <!-- Hour labels row -->
            <div class="schedule-row schedule-row--header">
                <div class="schedule-day-label"></div>
                <div v-for="h in 24" :key="'h' + h" class="schedule-hour-label">
                    {{ (h - 1).toString().padStart(2, '0') }}
                </div>
                <div v-if="showsFrequencyInputs && editable" class="schedule-day-step-spacer" aria-hidden="true"></div>
                <div v-if="showApplyDayButtons" class="schedule-day-action-spacer"></div>
            </div>

            <!-- Day rows -->
            <div v-for="(dayName, dayNum) in visibleDays" :key="dayNum" class="schedule-row">
                <div class="schedule-day-label">{{ dayName }}</div>
                <div v-for="h in 24" :key="h" class="schedule-cell" :class="{
                    active: isHourActive(dayNum, h),
                    disabled: isHourDisallowed(dayNum, h),
                    'schedule-cell--readonly': !editable,
                    'schedule-cell--override': isFrequencyOverride(dayNum, h),
                }"
                    :title="cellTitle(dayName, dayNum, h)"
                    @click="toggleHour(dayNum, h)">
                    <template v-if="showsFrequencyInputs && editable && isHourActive(dayNum, h)">
                        <div class="schedule-admin-wrapper">
                            <input type="number" class="schedule-input" v-model.number="adminValues[`${dayNum}:${h}`]"
                                :min="1" :max="60" @input="onAdminInput(dayNum, h, $event.target.value)" @click.stop />
                        </div>
                    </template>
                </div>
                <div v-if="showsFrequencyInputs && editable" class="schedule-day-step">
                    <button type="button" class="schedule-cell schedule-cell--step" title="−1 ко всем активным часам дня"
                        @click.stop="adjustDayFrequencies(Number(dayNum), -1)">−</button>
                    <button type="button" class="schedule-cell schedule-cell--step" title="+1 ко всем активным часам дня"
                        @click.stop="adjustDayFrequencies(Number(dayNum), 1)">+</button>
                </div>
                <div v-if="showApplyDayButtons" class="schedule-day-action">
                    <button type="button" class="schedule-btn schedule-btn--apply"
                        :disabled="!editable || applyingDay === Number(dayNum)"
                        @click.stop="onApplyDay(Number(dayNum), dayName)">
                        {{ applyingDay === Number(dayNum) ? '…' : 'Применить' }}
                    </button>
                </div>
            </div>
        </div>

        <div v-if="Object.keys(visibleDays).length > 0 && editable" class="schedule-actions">
            <button type="button" class="schedule-btn" @click="selectAll">Выбрать все</button>
            <button type="button" class="schedule-btn" @click="clearAll">Очистить</button>
            <div v-if="showsFrequencyInputs" class="schedule-admin-hint">
                <span class="schedule-hint-examples" aria-hidden="true">
                    <span class="schedule-hint-example">
                        <span class="schedule-hint-swatch"></span>
                        <span class="schedule-hint-example-label">выкл</span>
                    </span>
                    <span class="schedule-hint-example">
                        <span class="schedule-hint-swatch schedule-hint-swatch--active">5</span>
                        <span class="schedule-hint-example-label">5 мин</span>
                    </span>
                    <span v-if="hasFrameBinding" class="schedule-hint-example">
                        <span class="schedule-hint-swatch schedule-hint-swatch--override">5</span>
                        <span class="schedule-hint-example-label">на связке ≠ шаблон</span>
                    </span>
                </span>
                <span class="schedule-hint-text">
                    1–60 — минуты между показами; ввод 0 выключает час.
                </span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'RtbScheduleGrid',
    props: {
        modelValue: {
            type: Array,
            default: () => [],
        },
        // Parent campaign hours used as an allow-mask on creative×frame grids.
        // Not passed (undefined) on the campaign-level editor — that is not a mask.
        campaignSchedule: {
            type: Array,
            default: undefined,
        },
        startDate: {
            type: String,
            default: '',
        },
        endDate: {
            type: String,
            default: '',
        },
        editable: {
            type: Boolean,
            default: true,
        },
        campaignId: {
            type: [Number, String],
            default: null,
        },
        creativeId: {
            type: [Number, String],
            default: null,
        },
        screenId: {
            type: [Number, String],
            default: null,
        },
        creativeLengthSeconds: {
            type: Number,
            default: 0,
        },
        isAdmin: {
            type: Boolean,
            default: false,
        },
        // Optional: if parent passes frequencies array, use it instead of fetching from server.
        initialFrequencies: {
            type: Array,
            default: null,
        },
        // Campaign-level template used as baseline for orange override highlighting on bindings.
        baselineFrequencies: {
            type: Array,
            default: null,
        },
        // Show per-day "Применить" on campaign-level schedule (edit mode only).
        showApplyDayButtons: {
            type: Boolean,
            default: false,
        },
        applyingDay: {
            type: Number,
            default: null,
        },
    },
    emits: ['update:modelValue', 'frequencies-updated', 'apply-day'],
    data() {
        return {
            allDays: {
                1: 'Пн',
                2: 'Вт',
                3: 'Ср',
                4: 'Чт',
                5: 'Пт',
                6: 'Сб',
                7: 'Вс',
            },
            // Internal flat set: "day:hour" => true/false
            activeMap: {},
            initialized: false,
            // admin per-cell values: key "day:hour" (day 1..7, hour 1..24)
            adminValues: {},
            // allowed by campaign: "day:hour" => true/false
            allowedMap: {},
            baselineMap: {},
        };
    },
    async mounted() {
        this.loadCaps();
        this.loadBaseline();
    },
    computed: {
        visibleDays() {
            if (!this.startDate || !this.endDate) {
                return this.allDays;
            }
            const start = new Date(this.startDate);
            const end = new Date(this.endDate);
            if (isNaN(start.getTime()) || isNaN(end.getTime())) {
                return this.allDays;
            }

            const daySet = new Set();
            const current = new Date(start);
            // Limit iteration to prevent infinite loop (max 1 year)
            const maxIter = 366;
            let i = 0;
            while (current <= end && i < maxIter) {
                // getDay(): 0=Sun, 1=Mon... => ISO: Mon=1, Sun=7
                const jsDay = current.getDay();
                const isoDay = jsDay === 0 ? 7 : jsDay;
                daySet.add(isoDay);
                if (daySet.size === 7) break;
                current.setDate(current.getDate() + 1);
                i++;
            }

            const result = {};
            for (const d of daySet) {
                result[d] = this.allDays[d];
            }
            return result;
        },
        totalAvailableHours() {
            return Object.keys(this.visibleDays).length * 24;
        },
        activeHoursTotal() {
            let count = 0;
            for (const dayNum of Object.keys(this.visibleDays)) {
                for (let h = 1; h <= 24; h++) {
                    if (this.activeMap[`${dayNum}:${h}`]) count++;
                }
            }
            return count;
        },
        hasFrameBinding() {
            return this.creativeId != null && this.creativeId !== ''
                && this.screenId != null && this.screenId !== '';
        },
        showsFrequencyInputs() {
            return !!this.isAdmin;
        },
        hasCampaignMask() {
            return Array.isArray(this.campaignSchedule) && this.campaignSchedule.length > 0;
        },
    },
    watch: {
        modelValue: {
            handler(val) {
                this.importIntervals(val);
            },
            immediate: true,
            deep: true,
        },
        visibleDays(newDays) {
            const map = { ...this.activeMap };
            let changed = false;
            const defaultOn = !this.initialized && (!this.modelValue || !this.modelValue.length);
            for (const dStr of Object.keys(newDays)) {
                let hasAny = false;
                for (let h = 1; h <= 24; h++) {
                    if (map[`${dStr}:${h}`] !== undefined) { hasAny = true; break; }
                }
                if (!hasAny) {
                    for (let h = 1; h <= 24; h++) {
                        map[`${dStr}:${h}`] = defaultOn;
                    }
                    changed = true;
                }
            }
            if (!changed) return;
            this.activeMap = map;
            this.clampActiveToAllowed();
            if (defaultOn) this.emitIntervals();
        },
        campaignSchedule: {
            handler() {
                this.applyCampaignMask();
            },
            immediate: true,
            deep: true,
        },
        initialFrequencies: {
            handler() {
                this.loadCaps();
            },
            deep: true,
        },
        baselineFrequencies: {
            handler() {
                this.loadBaseline();
            },
            deep: true,
        },
    },
    methods: {
        hourRangeFromInterval(iv) {
            const startH = Math.floor(Number(iv.startTime) / 3600000);
            const endH = Math.floor(Number(iv.endTime) / 3600000);
            return { startH, endH };
        },
        markIntervalHours(map, intervals, value) {
            for (const iv of intervals || []) {
                const { startH, endH } = this.hourRangeFromInterval(iv);
                const day = iv.day;
                if (day === undefined || day === null) {
                    for (const dStr of Object.keys(this.visibleDays)) {
                        for (let h = startH; h < endH; h++) {
                            map[`${dStr}:${h + 1}`] = value;
                        }
                    }
                } else {
                    for (let h = startH; h < endH; h++) {
                        if (this.visibleDays[String(day)] !== undefined) {
                            map[`${day}:${h + 1}`] = value;
                        }
                    }
                }
            }
        },
        buildAllowedMap() {
            const allowed = {};
            const useMask = this.hasCampaignMask;
            for (const dStr of Object.keys(this.visibleDays)) {
                for (let h = 1; h <= 24; h++) {
                    allowed[`${dStr}:${h}`] = !useMask;
                }
            }
            if (useMask) this.markIntervalHours(allowed, this.campaignSchedule, true);
            return allowed;
        },
        clampActiveToAllowed() {
            if (!this.allowedMap || Object.keys(this.allowedMap).length === 0) return;
            const map = { ...this.activeMap };
            let changed = false;
            for (const key of Object.keys(this.allowedMap)) {
                if (this.allowedMap[key] === false && map[key]) {
                    map[key] = false;
                    changed = true;
                }
            }
            if (changed) this.activeMap = map;
        },
        /** Convert intervals array to flat hour map. modelValue is the source of truth for selection. */
        importIntervals(intervals) {
            const map = {};
            if (!intervals || !intervals.length) {
                if (this.initialized) {
                    this.activeMap = map;
                    this.clampActiveToAllowed();
                    return;
                }
                // First init with no saved schedule: CCF inherits campaign hours; campaign editor defaults all-on.
                if (this.hasCampaignMask) {
                    for (const dStr of Object.keys(this.visibleDays)) {
                        for (let h = 1; h <= 24; h++) {
                            map[`${dStr}:${h}`] = false;
                        }
                    }
                    this.markIntervalHours(map, this.campaignSchedule, true);
                } else {
                    for (const dStr of Object.keys(this.visibleDays)) {
                        for (let h = 1; h <= 24; h++) {
                            map[`${dStr}:${h}`] = true;
                        }
                    }
                }
            } else {
                this.initialized = true;
                for (const iv of intervals) {
                    const { startH, endH } = this.hourRangeFromInterval(iv);
                    const day = iv.day;
                    for (let h = startH; h < endH; h++) {
                        map[`${day}:${h + 1}`] = true;
                    }
                }
            }
            this.activeMap = map;
            this.clampActiveToAllowed();
        },
        /**
         * Campaign schedule is an allow-mask only. Never copies the mask into activeMap.
         * Hours outside the mask are deactivated; newly allowed hours stay off until the user selects them.
         */
        applyCampaignMask() {
            this.allowedMap = this.buildAllowedMap();
            this.clampActiveToAllowed();

            if (!this.showsFrequencyInputs) return;

            for (const dStr of Object.keys(this.visibleDays)) {
                for (let h = 1; h <= 24; h++) {
                    const key = `${dStr}:${h}`;
                    if (!this.allowedMap[key]) this.adminValues[key] = null;
                }
            }
        },
        isHourActive(day, hour) {
            return !!this.activeMap[`${day}:${hour}`];
        },
        isHourDisallowed(day, hour) {
            return this.allowedMap[`${day}:${hour}`] === false;
        },
        minutesFromMs(ms) {
            if (ms === null || ms === undefined) return null;
            return Math.floor(Number(ms) / 60000);
        },
        loadBaseline() {
            const map = {};
            if (Array.isArray(this.baselineFrequencies)) {
                for (const m of this.baselineFrequencies) {
                    if (m.day_of_week === null || m.day_of_week === undefined) continue;
                    const key = `${Number(m.day_of_week) + 1}:${Number(m.hour) + 1}`;
                    map[key] = this.minutesFromMs(m.min_interval_ms);
                }
            }
            this.baselineMap = map;
        },
        isFrequencyOverride(day, hour) {
            if (!this.hasFrameBinding || !this.showsFrequencyInputs) return false;
            if (!this.isHourActive(day, hour)) return false;
            const key = `${day}:${hour}`;
            const current = this.adminValues[key];
            if (current === null || current === undefined || current === '') return false;
            if (!Object.prototype.hasOwnProperty.call(this.baselineMap, key)) return false;
            const baseline = this.baselineMap[key];
            if (baseline === null || baseline === undefined) return false;
            return Number(current) !== Number(baseline);
        },
        cellTitle(dayName, day, hour) {
            let title = `${dayName} ${(hour - 1).toString().padStart(2, '0')}:00–${hour.toString().padStart(2, '0')}:00`;
            if (!this.isAdmin) return title;
            const val = this.adminValues[`${day}:${hour}`];
            if (val === null || val === undefined || val === '') return title;
            if (val === 0) title += '. Запрещено';
            else title += `. 1 показ в ${val} минут`;
            if (this.isFrequencyOverride(day, hour)) title += ' (отличается от шаблона)';
            return title;
        },
        toggleHour(day, hour) {
            if (!this.editable) {
                return;
            }
            const key = `${day}:${hour}`;
            // Respect campaign-level allowed mask
            if (this.isHourDisallowed(day, hour)) {
                return;
            }
            const nextActive = !this.activeMap[key];
            this.activeMap = { ...this.activeMap, [key]: nextActive };
            if (nextActive && this.showsFrequencyInputs) {
                const current = this.adminValues[key];
                if (current === null || current === undefined || current === '') {
                    this.adminValues[key] = this.defaultMinutesForIso(Number(day), hour);
                }
            }
            this.emitIntervals();
            if (this.showsFrequencyInputs) this._doEmit();
        },
        defaultMinutesForIso(isoDay, hour1to24) {
            const dow = isoDay - 1;
            const hour0 = hour1to24 - 1;
            if (dow >= 0 && dow <= 4) {
                if ((hour0 >= 7 && hour0 < 10) || hour0 === 13 || (hour0 >= 17 && hour0 < 21)) {
                    return 3;
                }
                return 6;
            }
            if (hour0 >= 11 && hour0 < 22) {
                return 3;
            }
            return 6;
        },
        selectAll() {
            if (!this.editable) {
                return;
            }
            const map = { ...this.activeMap };
            // Respect campaign-level allowedMap when present: only activate allowed cells.
            const useAllowed = this.allowedMap && Object.keys(this.allowedMap).length > 0;
            for (const dayNum of Object.keys(this.visibleDays)) {
                for (let h = 1; h <= 24; h++) {
                    const key = `${dayNum}:${h}`;
                    map[key] = useAllowed ? !!this.allowedMap[key] : true;
                    if (map[key] && this.showsFrequencyInputs) {
                        const current = this.adminValues[key];
                        if (current === null || current === undefined || current === '') {
                            this.adminValues[key] = this.defaultMinutesForIso(Number(dayNum), h);
                        }
                    }
                }
            }
            this.activeMap = map;
            this.initialized = true;
            this.emitIntervals();
            if (this.showsFrequencyInputs) this._doEmit();
        },
        clearAll() {
            if (!this.editable) {
                return;
            }
            const map = { ...this.activeMap };
            for (const dayNum of Object.keys(this.visibleDays)) {
                for (let h = 1; h <= 24; h++) {
                    const key = `${dayNum}:${h}`;
                    // make hour inactive
                    map[key] = false;
                    // clear admin value for this cell so frequencies are removed as well
                    if (this.adminValues && Object.prototype.hasOwnProperty.call(this.adminValues, key)) {
                        this.adminValues[key] = null;
                    }
                }
            }
            this.activeMap = map;
            this.initialized = true;
            this.emitIntervals();
            if (this.showsFrequencyInputs) this._doEmit();
        },
        loadCaps() {
            for (const dStr of Object.keys(this.visibleDays)) {
                const d = Number(dStr);
                for (let h = 1; h <= 24; h++) {
                    this.adminValues[`${d}:${h}`] = null;
                }
            }

            if (!this.initialFrequencies || !Array.isArray(this.initialFrequencies) || this.initialFrequencies.length === 0) {
                return;
            }

            for (const m of this.initialFrequencies) {
                const dow = m.day_of_week;
                const hour0 = m.hour;
                const value = (m.min_interval_ms !== undefined && m.min_interval_ms !== null)
                    ? Math.floor(m.min_interval_ms / 60000)
                    : (m.max_per_minute ? Math.floor(60 / m.max_per_minute) : null);

                if (dow === null || dow === undefined) {
                    for (let d = 1; d <= 7; d++) {
                        this.adminValues[`${d}:${hour0 + 1}`] = value === undefined ? null : value;
                    }
                } else {
                    this.adminValues[`${dow + 1}:${hour0 + 1}`] = value === undefined ? null : value;
                }
            }
        },

        // handle admin input change: clamp and emit updated frequencies to parent
        onAdminInput(day, hour, rawValue) {
            const key = `${day}:${hour}`;
            let v = rawValue === '' ? null : Number(rawValue);
            if (v !== null && !Number.isFinite(v)) v = null;

            // Typing 0 turns the hour off (same as clicking the cell), no forbid-cap write.
            if (v === 0) {
                this.activeMap = { ...this.activeMap, [key]: false };
                this.adminValues[key] = null;
                this.emitIntervals();
                this._doEmit();
                return;
            }

            if (v !== null) {
                if (v > 60) v = 60;
                if (v < 1) v = 1;
            }
            this.adminValues[key] = v;
            this.scheduleEmit();
        },

        /**
         * ±1 for all active numeric cells in a day row.
         * Minus from 1 (or legacy 0) deactivates the hour.
         */
        adjustDayFrequencies(isoDay, delta) {
            if (!this.editable || !this.showsFrequencyInputs) return;
            if (delta !== 1 && delta !== -1) return;

            let scheduleChanged = false;
            const nextActive = { ...this.activeMap };
            const nextAdmin = { ...this.adminValues };

            for (let h = 1; h <= 24; h++) {
                const key = `${isoDay}:${h}`;
                if (!nextActive[key]) continue;

                const raw = nextAdmin[key];
                if (raw === '' || raw === null || raw === undefined) continue;
                const current = Number(raw);
                if (!Number.isFinite(current)) continue;

                if (delta === 1) {
                    nextAdmin[key] = Math.min(60, current + 1);
                    continue;
                }

                // delta === -1
                if (current <= 1) {
                    nextActive[key] = false;
                    nextAdmin[key] = null;
                    scheduleChanged = true;
                } else {
                    nextAdmin[key] = current - 1;
                }
            }

            this.activeMap = nextActive;
            this.adminValues = nextAdmin;
            if (scheduleChanged) this.emitIntervals();
            this._doEmit();
        },

        // debounced emit: schedule a delayed emit to parent to avoid frequent updates
        scheduleEmit() {
            if (this._emitTimer) clearTimeout(this._emitTimer);
            this._emitTimer = setTimeout(() => this._doEmit(), 250);
        },

        collectFrequencyValues(dayFilter = null) {
            const values = [];
            for (const dStr of Object.keys(this.visibleDays)) {
                const d = Number(dStr);
                if (dayFilter !== null && d !== dayFilter) continue;
                for (let h = 1; h <= 24; h++) {
                    if (!this.isHourActive(d, h)) continue;
                    const key = `${d}:${h}`;
                    const raw = this.adminValues[key];
                    const val = (raw === '' || raw === null || raw === undefined) ? null : Number(raw);
                    values.push({
                        day_of_week: d - 1,
                        hour: h - 1,
                        min_interval_ms: val !== null ? (val * 60000) : null,
                    });
                }
            }
            return values;
        },

        // immediate emit helper
        _doEmit() {
            const values = this.collectFrequencyValues();

            // emit with binding ids so parent can save
            // use `gid` field name (backend expects `gid`)
            this.$emit('frequencies-updated', {
                campaign_id: this.campaignId,
                creative_id: this.creativeId,
                gid: this.screenId,
                values,
            });
        },
        onApplyDay(isoDay, dayName) {
            const values = this.collectFrequencyValues(isoDay).map((cell) => ({
                hour: cell.hour,
                min_interval_ms: cell.min_interval_ms,
            }));
            this.$emit('apply-day', {
                day_of_week: isoDay - 1,
                day_name: dayName,
                values,
            });
        },
        // Note: campaign-level schedule restrictions are intentionally not applied here.
        /** Convert flat hour map back to intervals (only visible days) */
        emitIntervals() {
            const intervals = [];
            for (const dStr of Object.keys(this.visibleDays)) {
                const d = Number(dStr);
                let startH = null;
                for (let h = 1; h <= 25; h++) {
                    const active = h <= 24 && !!this.activeMap[`${d}:${h}`];
                    if (active && startH === null) {
                        startH = h - 1;
                    } else if (!active && startH !== null) {
                        intervals.push({
                            day: d,
                            startTime: startH * 3600000,
                            endTime: (h - 1) * 3600000,
                        });
                        startH = null;
                    }
                }
            }
            this.$emit('update:modelValue', intervals);
        },
    },
};
</script>

<style scoped>
.rtb-schedule-grid {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 10px;
    margin-top: 6px;
}

.schedule-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.schedule-title {
    font-weight: 600;
    font-size: 12px;
    color: #374151;
}

.schedule-hint {
    font-size: 11px;
    color: #6b7280;
}

.schedule-empty {
    font-size: 12px;
    color: #9ca3af;
    text-align: center;
    padding: 12px 0;
}

.schedule-table {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.schedule-row {
    display: flex;
    align-items: center;
    gap: 2px;
}

.schedule-row--header {
    margin-bottom: 2px;
}

.schedule-day-label {
    width: 28px;
    font-size: 11px;
    font-weight: 500;
    color: #6b7280;
    text-align: right;
    padding-right: 4px;
    flex-shrink: 0;
}

.schedule-hour-label {
    width: 17px;
    text-align: center;
    font-size: 8px;
    color: #9ca3af;
    font-weight: 500;
    flex-shrink: 0;
}

.schedule-cell {
    width: 17px;
    height: 17px;
    border: 1px solid #d1d5db;
    border-radius: 3px;
    cursor: pointer;
    transition: all 0.12s;
    flex-shrink: 0;
    background: #fff;
    position: relative;
}

.schedule-cell:hover {
    border-color: #3b82f6;
}

.schedule-cell--readonly {
    cursor: default;
}

.schedule-cell--readonly:hover {
    border-color: #d1d5db;
}

.schedule-cell.active {
    background: #10b981;
    border-color: #10b981;
}

.schedule-cell.active.schedule-cell--override {
    background: #f97316;
    border-color: #ea580c;
}

.schedule-cell.disabled {
    background: #f3f4f6;
    border-color: #e5e7eb;
    cursor: not-allowed;
    opacity: 0.7;
}

.schedule-day-action,
.schedule-day-action-spacer {
    width: 72px;
    flex-shrink: 0;
    margin-left: 4px;
}

.schedule-day-step,
.schedule-day-step-spacer {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    width: 32px;
    flex-shrink: 0;
    margin-left: 4px;
}

.schedule-cell--step {
    width: 14px;
    height: 14px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    color: #374151;
    background: #f9fafb;
    cursor: pointer;
}

.schedule-cell--step:hover {
    border-color: #3b82f6;
    background: #eff6ff;
    color: #1d4ed8;
}

.schedule-day-action {
    display: flex;
    align-items: center;
}

.schedule-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
    align-items: center;
}

.schedule-btn {
    padding: 3px 10px;
    font-size: 11px;
    font-weight: 500;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background: #f9fafb;
    color: #374151;
    cursor: pointer;
    transition: all 0.15s;
}

.schedule-btn:hover:not(:disabled) {
    background: #f3f4f6;
    border-color: #9ca3af;
}

.schedule-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.schedule-btn--apply {
    padding: 2px 6px;
    font-size: 10px;
    white-space: nowrap;
}

.schedule-btn--primary {
    background: #10b981;
    border-color: #059669;
    color: #fff;
}

.schedule-btn--primary:hover:not(:disabled) {
    background: #059669;
    border-color: #047857;
    color: #fff;
}

.schedule-input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: none;
    background: transparent;
    color: #ffffff;
    text-align: center;
    padding: 0;
    margin: 0;
    font-size: 10px;
    line-height: 17px;
}

.schedule-input::-webkit-outer-spin-button,
.schedule-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.schedule-admin-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
}

.schedule-admin-hint {
    margin-left: 8px;
    font-size: 12px;
    color: #6b7280;
    align-self: center;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
}

.schedule-hint-examples {
    display: inline-flex;
    align-items: center;
    gap: 10px;
}

.schedule-hint-example {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.schedule-hint-swatch {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 17px;
    height: 17px;
    border: 1px solid #d1d5db;
    border-radius: 3px;
    background: #fff;
    color: #fff;
    font-size: 10px;
    line-height: 17px;
    flex-shrink: 0;
}

.schedule-hint-swatch--active {
    background: #10b981;
    border-color: #10b981;
}

.schedule-hint-swatch--override {
    background: #f97316;
    border-color: #ea580c;
}

.schedule-hint-example-label {
    font-size: 11px;
    color: #6b7280;
    white-space: nowrap;
}

.schedule-hint-text {
    font-size: 11px;
    color: #6b7280;
}
</style>
